import React from 'react';
import { FiGift, FiUpload, FiInfo, FiPlus } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Callout, calloutVariants } from '@components/Callout';
import { Dropdown } from '@components/Dropdown';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import { HolidayListEditor } from './HolidayListEditor.component';
import { HolidayFormModal } from './HolidayFormModal.component';
import { HolidayImportModal } from './HolidayImportModal.component';

import styles from './HolidaysSection.module.scss';

// Pure presentational — no Redux/service calls. Rendered by both
// HolidaysSection.component.jsx (real, Firestore-backed) and
// HolidaysSection.demo.component.jsx (static fixture-backed); a future
// redesign only ever needs to touch this file plus whichever container's
// props it changes.
export const HolidaysSectionView = ({
  contextOptions,
  selectedContext,
  onContextChange,
  isTeamContext,
  isPersisting,
  showContextSwitcher = true,
  showImportButton,
  holidays,
  onEdit,
  onRemove,
  onAdd,
  isModalOpen,
  onCloseModal,
  onSubmitModal,
  editingHoliday,
  isImportModalOpen,
  onOpenImportModal,
  onCloseImportModal,
  onImportRows,
}) => {
  const existingDates = holidays.map((holiday) => holiday.date);

  return (
    <>
      <SettingsPanelHeader
        icon={<FiGift size={20} color="var(--indigo-600)" />}
        title="Holidays"
        description="Manage the public and festival holidays your company observes each year."
        rightSlot={
          showContextSwitcher && (
            <Dropdown
              options={contextOptions}
              value={selectedContext}
              onChange={onContextChange}
              isDisabled={isPersisting}
              width="220px"
              selectStyles={{
                control: { height: '34px' },
                valueContainer: { height: '34px' },
              }}
            />
          )
        }
      />

      <div className={styles.sectionHeading}>
        <div className={styles.sectionHeadingText}>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            {new Date().getFullYear()} Holiday List
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            {isTeamContext
              ? `Holidays for ${selectedContext.label}. Starts from the company list - edit or remove a date to customize it just for this department.`
              : "These dates won't count as absences for any employee."}
          </Text>
        </div>
        <div className={styles.sectionHeadingActions}>
          {showImportButton && (
            <Button
              text="Import Holidays"
              variant={buttonVariants.outline}
              icon={<FiUpload size={14} />}
              onClick={onOpenImportModal}
            />
          )}
          <Button
            text="Add Holiday"
            variant={buttonVariants.primary}
            icon={<FiPlus size={14} />}
            onClick={onAdd}
          />
        </div>
      </div>

      <HolidayListEditor
        holidays={holidays}
        onEdit={onEdit}
        onRemove={onRemove}
        onAdd={onAdd}
        disabled={isPersisting}
      />

      <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
        Changes will be reflected in employee calendars and attendance reports.
      </Callout>

      <HolidayFormModal
        isVisible={isModalOpen}
        onClose={onCloseModal}
        onSubmit={onSubmitModal}
        initialHoliday={editingHoliday}
        existingDates={existingDates}
        isSaving={isPersisting}
      />

      <HolidayImportModal
        isVisible={isImportModalOpen}
        onClose={onCloseImportModal}
        onImport={onImportRows}
        existingDates={existingDates}
      />
    </>
  );
};
